import EditIcon from "@mui/icons-material/Edit"
import { Box, CircularProgress, MenuItem } from "@mui/material"
import Button from "@mui/material/Button"
import Dialog from "@mui/material/Dialog"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import DialogTitle from "@mui/material/DialogTitle"
import TextField from "@mui/material/TextField"
import { useFormik } from "formik"
import { useAppDispatch } from "hooks"
import { useState } from "react"
import userGroupService from "services/userGroupService"
import { updateUserGroup } from "slices/userGroupSlice"
import type { IUpdateUserGroup, IUserGroup } from "types/userGroupTypes"
import * as Yup from "yup"

type Props = {
  userGroup: IUserGroup
}

export function UpdateUserGroupDialog({ userGroup }: Props) {
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [open, setOpen] = useState(false)

  const dispatch = useAppDispatch()

  const initialValues: IUpdateUserGroup = {
    name: userGroup.name,
  }

  const updateUserGroupValidationScheme = Yup.object({
    name: Yup.string()
      .required("Please enter user group name")
      .max(64, "Maximum length is 64 characters"),
  })

  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: updateUserGroupValidationScheme,
    onSubmit: (values) => {
      setIsLoading(true)

      const userGroupUpdate: IUpdateUserGroup = {
        name: values.name,
      }

      userGroupService
        .updateUserGroup(userGroup.id, userGroupUpdate)
        .then((resp) => {
          dispatch(updateUserGroup(resp))
          setIsLoading(false)
          handleClose()
        })
        .catch((err) => {
          console.log(err)
          setIsLoading(false)
        })
    },
  })

  const handleClickOpen = () => {
    formik.resetForm()
    setOpen(true)
  }

  const handleClose = () => {
    setOpen(false)
  }

  return (
    <div>
      <MenuItem onClick={handleClickOpen}>
        <EditIcon sx={{ marginRight: 1 }} />
        Update
      </MenuItem>
      <Dialog open={open} fullWidth maxWidth="sm">
        <form onSubmit={formik.handleSubmit}>
          <DialogTitle>Update User Group</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              id="name"
              label="Name"
              type="name"
              fullWidth
              variant="outlined"
              value={formik.values.name}
              onChange={formik.handleChange}
              error={formik.touched.name && Boolean(formik.errors.name)}
              helperText={formik.touched.name && formik.errors.name}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>Cancel</Button>
            <Box sx={{ m: 1, position: "relative" }}>
              <Button type="submit" disabled={isLoading}>
                Update
              </Button>
              {isLoading && (
                <CircularProgress
                  size={24}
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    marginTop: "-12px",
                    marginLeft: "-12px",
                  }}
                />
              )}
            </Box>
          </DialogActions>
        </form>
      </Dialog>
    </div>
  )
}
